import type { GenericTrackingEvent, GtmEventEECproductClick } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

export const SearchProductCardClickTrackingResultFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardClickTrackingResultFragment on SearchProductResult {
    categories {
      name
      isSelected
    }
    ... on ProperCategoryQueryResult {
      breadcrumbs {
        name
        pathName
      }
    }
  }
`);

export const SearchProductCardClickTrackingItemFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardClickTrackingItemFragment on StylePreviewItem {
    styleName
    brand {
      name
    }
    listPosition
  }
`);

export const SearchProductCardClickTrackingVariationGroupFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardClickTrackingVariationGroupFragment on CompleteVariationGroup {
    articleNumber
    price {
      currentPrice
    }
  }
`);

export const useProductClickTracking = (
  maskedResult: FragmentType<typeof SearchProductCardClickTrackingResultFragment>,
  maskedItem: FragmentType<typeof SearchProductCardClickTrackingItemFragment>,
  maskedVariationGroup: FragmentType<typeof SearchProductCardClickTrackingVariationGroupFragment>,
) => {
  const {
    brand: { name: brand },
    styleName,
    listPosition,
  } = getFragmentData(SearchProductCardClickTrackingItemFragment, maskedItem);
  const {
    articleNumber,
    price: { currentPrice },
  } = getFragmentData(SearchProductCardClickTrackingVariationGroupFragment, maskedVariationGroup);
  const result = getFragmentData(SearchProductCardClickTrackingResultFragment, maskedResult);

  const currentCategoryName = result.categories.find((category) => category.isSelected)?.name;

  const dispatchGTMEvent = useTracking();

  const customCategory =
    'breadcrumbs' in result && result.breadcrumbs
      ? result.breadcrumbs
          .filter((item) => item.pathName !== '/')
          .map(({ name }) => name)
          .join('/')
      : '';

  const listId = currentCategoryName ? `Category: ${currentCategoryName}` : 'Search Result';

  const dispatchProductClickTracking = () => {
    dispatchGTMEvent<GenericTrackingEvent>({
      event: 'suche-event',
      eventValue: `click=${listPosition};${articleNumber}`,
    });
    dispatchGTMEvent<GtmEventEECproductClick>({
      event: 'EECproductClick',
      ecommerce: {
        click: {
          actionField: {
            list: listId,
            action: 'click',
          },
          products: [
            {
              name: styleName,
              id: articleNumber,
              price: currentPrice / 100,
              category: customCategory,
              brand,
              position: listPosition ?? 0,
            },
          ],
        },
      },
    });
  };

  return {
    dispatchProductClickTracking,
  };
};
